import React, { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { NonIdealState } from '@blueprintjs/core';
import moment from 'moment';

import { Button } from 'components/buttons';
import { EChartsRenderer } from 'components/charts';
import { ShowHide } from 'components/layout';

import { formatValue, readFailedMessage, retryText } from './diffViewerFormatting';

export const trendSeriesNames = {
    newDifferences: "New",
    stillOpen: "Still open"
};

export const trendDayAxisName = "Day (UK local)";
const isoDateFormat = "YYYY-MM-DD";
const periodDateFormat = "D MMMM YYYY";
const gateMatchText = "When the period ends today, the last Still open point matches the exit gate's Still open figure while Show provisional is on.";
const trendFailedHeading = "The daily trend could not be loaded";
const pastDaysText = "Past days reflect each difference's current status.";

const defaultArray = [];
const defaultFunction = () => null;

function formatAnalysedDrivers(day, missingDriversText) {
    if (day.analysedDrivers == null) {
        return missingDriversText;
    }

    return formatValue(day.analysedDrivers);
}

function formatPeriod(period) {
    const from = moment(period.from, isoDateFormat).format(periodDateFormat);
    const to = moment(period.to, isoDateFormat).format(periodDateFormat);

    return `${from} to ${to}, by UK-local day. ${gateMatchText}`;
}

export function buildTrendTooltip(days, params, missingDriversText) {
    const index = params[0]?.dataIndex;
    const day = days[index];

    if (day == null) {
        return "";
    }

    return [
        day.date,
        `${trendSeriesNames.newDifferences}: ${day.newCount}`,
        `${trendSeriesNames.stillOpen}: ${day.stillOpen}`,
        `Drivers analysed: ${formatAnalysedDrivers(day, missingDriversText)}`
    ].join("<br/>");
}

function buildTrendOption(days, missingDriversText) {
    return {
        legend: {
            top: 0,
            data: [trendSeriesNames.newDifferences, trendSeriesNames.stillOpen]
        },
        tooltip: {
            trigger: 'axis',
            formatter: (params) => buildTrendTooltip(days, params, missingDriversText)
        },
        grid: {
            top: 40,
            left: 40,
            right: 20,
            bottom: 60
        },
        xAxis: {
            type: 'category',
            name: trendDayAxisName,
            nameLocation: 'middle',
            nameGap: 35,
            data: days.map((day) => day.date)
        },
        yAxis: {
            type: 'value',
            minInterval: 1
        },
        series: [
            {
                name: trendSeriesNames.newDifferences,
                type: 'line',
                data: days.map((day) => day.newCount)
            },
            {
                name: trendSeriesNames.stillOpen,
                type: 'line',
                data: days.map((day) => day.stillOpen)
            }
        ]
    };
}

/**
 * The differences found and still open on each UK-local day of the period. Its read is its own, so a
 * failure here says so and leaves the rest of the page as it is.
 */
export function DiffViewerTrend(
    {
        days = defaultArray,
        loading = false,
        failed = false,
        period = null,
        missingDriversText = "",
        unavailableMessage = null,
        onRetry = defaultFunction
    }
) {
    const option = useMemo(() => buildTrendOption(days, missingDriversText), [days, missingDriversText]);

    return (
        <div id="rules-engine-diff-trend" className="spacer-bottom">
            <h3 id="rules-engine-diff-trend-heading">Daily trend</h3>
            <ShowHide
                evaluator={period != null && unavailableMessage == null}
                show={(
                    <p id="rules-engine-diff-trend-period" className="description-text">
                        {period == null ? "" : formatPeriod(period)}
                    </p>
                )}
            />
            <ShowHide
                evaluator={unavailableMessage != null}
                show={(
                    <p id="rules-engine-diff-trend-unavailable">{unavailableMessage}</p>
                )}
                hide={(
                    <ShowHide
                        evaluator={failed}
                        show={(
                            <div className="list-table-no-results" id="rules-engine-diff-trend-failed">
                                <NonIdealState
                                    title={trendFailedHeading}
                                    description={readFailedMessage}
                                    action={(
                                        <Button id="rules-engine-diff-trend-retry" text={retryText} icon="refresh" onClick={onRetry} />
                                    )}
                                />
                            </div>
                        )}
                        hide={(
                            <Fragment>
                                <div id="rules-engine-diff-trend-chart" className={classNames({ "bp6-skeleton": loading })}>
                                    <EChartsRenderer option={option} notMerge />
                                </div>
                                <p id="rules-engine-diff-trend-past-days" className="description-text">{pastDaysText}</p>
                            </Fragment>
                        )}
                    />
                )}
            />
        </div>
    );
}

DiffViewerTrend.propTypes = {
    days: PropTypes.array,
    loading: PropTypes.bool,
    failed: PropTypes.bool,
    period: PropTypes.shape({
        from: PropTypes.string,
        to: PropTypes.string
    }),
    missingDriversText: PropTypes.string,
    unavailableMessage: PropTypes.string,
    onRetry: PropTypes.func
};
